import  { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faChevronCircleLeft, faPlus } from '@fortawesome/free-solid-svg-icons';
import {
    PageContainer,
    SectionTitle,
    Button,
    Divider,
    fadeIn,
    Footer,
    useGoBack,
    FormRow, FormGroup, Label, Input
} from '../styles/PageStyles';
import { useToast } from '../components/common/ToastProvider';
import { createPlanner, getPlannerByCode, updatePlanner } from '../services/plannerService';
import { getPipelines } from '../services/pipelineService';
import { MultiSelectDropdownSearch } from '../components/common/MultiSelectSearch';


const PlannerDetails = () => {
    const { plannerId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [plannerDetails, setPlannerDetail] = useState();
    const [toolPipelines, setToolPipelines] = useState([]);
    const [toolsLoading, setToolsLoading] = useState(false);
    const { showToast } = useToast();

    const loadToolPipelines = async () => {
        try {
            setToolsLoading(true);
            const queryString = 'skip=0&limit=100';
            const resp = await getPipelines({ type: 'tool' }, {}, queryString);
            const list = Array.isArray(resp) ? resp : [];
            setToolPipelines(list);
        } catch (error) {
            console.log('error while fetching tool pipelines', error);
        } finally {
            setToolsLoading(false);
        }
    };


    // For Model Current
    useEffect(() => {

        const fetchPlannerByCode = async () => {
            try {
                const responce = await getPlannerByCode(plannerId);
                setPlannerDetail({
                    ...responce,
                    tool_settings_list: responce.tool_settings_list || []
                })   
            } catch (error) {
             console.log("Error fetching planners", error)   
            }
        }

        if (plannerId !== 'new') {
            fetchPlannerByCode();
        } else {
            if (user) {
                setPlannerDetail({
                    code: "MDL-" + new Date().getTime(),
                    company_cd: user.companyCd,
                    name: "",
                    type: "",
                    status: "active",
                    provider: "",
                    tool_settings_list: []
                });
            }
        }

        loadToolPipelines();

    }, [plannerId]);


    const goBack = useGoBack();

    const savePlanner = () => {
        try {
            if (plannerDetails.id) {
                updatePlanner(plannerDetails);
            } else {
                console.log("Planner-->", plannerDetails)
                createPlanner(plannerDetails);
            }
            showToast({
                message: 'Planner updated successfully.',
                type: 'success'
            })
            navigate('/planner');
        } catch (error) {
            console.error('Error saving planner:', error);

        }
    }


    const handleAddToolSetting = () => {
        setPlannerDetail((prev) => ({
            ...prev,
            tool_settings_list: [
                ...(prev?.tool_settings_list || []),
                { type: 'mcp_internal', sub_type: 'pipeline', code: '' },
            ],
        }));
    };

    const handleUpdateToolSetting = (index, field, value) => {
        setPlannerDetail((prev) => {
            const list = [...(prev?.tool_settings_list || [])];
            const existing = list[index] || { type: 'mcp_internal', sub_type: 'pipeline', code: '' };
            list[index] = {
                ...existing,
                [field]: value,
            };

            if (field === 'type' && value === 'mcp_internal') {
                list[index].sub_type = 'pipeline';
            }
            return { ...prev, tool_settings_list: list };
        });
    };

    const handleRemoveToolSetting = (index) => {
        setPlannerDetail((prev) => {
            const list = [...(prev?.tool_settings_list || [])];
            list.splice(index, 1);
            return { ...prev, tool_settings_list: list };
        });
    };


    return (
        <PageContainer>
            <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeIn}
            >
                <SectionTitle>
                    <Button onClick={goBack} secondary ><FontAwesomeIcon icon={faChevronCircleLeft} /></Button>
                    {plannerDetails?.name ? (
                        <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                            {plannerDetails?.name}
                        </span>
                    ) : (
                        <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                            Add Planner
                        </span>
                    )}
                </SectionTitle>
                <Divider />
                <FormRow>
                    <FormGroup>
                        <Label>Name</Label>
                        <Input type="text" 
                               value={plannerDetails?.name || ''} 
                               onChange={(e) => setPlannerDetail({ ...plannerDetails, name: e.target.value })} />
                    </FormGroup>
                    <FormGroup>
                        <Label>Type</Label>
                        <Input type="text" 
                               value={plannerDetails?.type || ''} 
                               onChange={(e) => setPlannerDetail({ ...plannerDetails, type: e.target.value })} />
                    </FormGroup>
                    <FormGroup>
                        <Label>Status</Label>
                        <Input type="text" 
                               value={plannerDetails?.status || ''} 
                               onChange={(e) => setPlannerDetail({ ...plannerDetails, status: e.target.value })} />
                    </FormGroup>

                </FormRow>

                <FormRow>
                    <FormGroup style={{ width: '100%' }}>
                        <Label>Tools</Label>
                        {(() => {
                            const toolSettings = plannerDetails?.tool_settings_list || [];
                            const hasToolPipelines = Array.isArray(toolPipelines) && toolPipelines.length > 0;
                            const normalizedToolPipelines = (toolPipelines || []).map((p) => ({
                                ...p,
                                name: p.name || p.code,
                            }));

                            return (
                                <>
                                    {toolsLoading && (
                                        <div
                                            style={{
                                                fontSize: '0.9rem',
                                                color: 'var(--text-secondary)',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            Loading Internal Tools...
                                        </div>
                                    )}

                                    {!toolsLoading && !hasToolPipelines && (
                                        <div
                                            style={{
                                                fontSize: '0.9rem',
                                                color: 'var(--text-secondary)',
                                                marginBottom: '1rem',
                                            }}
                                        >
                                            No Internal Tools found. Create a pipeline of type{' '}
                                            <strong>tool</strong> in Agent Pipelines first.
                                        </div>
                                    )}

                                    {toolSettings.map((tool, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                marginBottom: '1rem',
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                border: '1px solid rgba(0, 102, 255, 0.1)',
                                                background: 'rgba(0, 102, 255, 0.02)',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr) auto',
                                                    gap: '0.75rem',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <div>
                                                    <Label style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                                                        Tool Type
                                                    </Label>
                                                    <select
                                                        value={tool.type || 'mcp_internal'}
                                                        onChange={(e) =>
                                                            handleUpdateToolSetting(index, 'type', e.target.value)
                                                        }
                                                        style={{
                                                            width: '100%',
                                                            padding: '0.6rem 0.8rem',
                                                            borderRadius: '8px',
                                                            border: '1px solid rgba(0, 102, 255, 0.1)',
                                                        }}
                                                    >
                                                        <option value="mcp_internal">Internal Tool</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <Label style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                                                        Internal Tool
                                                    </Label>
                                                    {hasToolPipelines ? (
                                                        <MultiSelectDropdownSearch
                                                            options={normalizedToolPipelines}
                                                            selected={
                                                                tool.code
                                                                    ? normalizedToolPipelines.filter((p) => p.code === tool.code)
                                                                    : []
                                                            }
                                                            onChange={(newSelection) => {
                                                                const last = newSelection[newSelection.length - 1];
                                                                const newCode = last ? last.code : '';
                                                                handleUpdateToolSetting(index, 'code', newCode);
                                                            }}
                                                            placeholder="Select Internal Tool"
                                                            searchPlaceholder="Search Internal Tools..."
                                                            isSingleSelect
                                                        />
                                                    ) : (
                                                        <select
                                                            disabled
                                                            style={{
                                                                width: '100%',
                                                                padding: '0.6rem 0.8rem',
                                                                borderRadius: '8px',
                                                                border: '1px solid rgba(0, 102, 255, 0.1)',
                                                            }}
                                                        >
                                                            <option value="">No Internal Tools available</option>
                                                        </select>
                                                    )}
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveToolSetting(index)}
                                                        style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            borderRadius: '6px',
                                                            border: '1px solid rgba(239,68,68,0.4)',
                                                            background: 'rgba(239,68,68,0.04)',
                                                            color: '#b91c1c',
                                                            fontSize: '0.8rem',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    <button
                                        type="button"
                                        onClick={handleAddToolSetting}
                                        style={{
                                            padding: '0.6rem 1rem',
                                            borderRadius: '6px',
                                            border: '1px dashed rgba(0, 102, 255, 0.5)',
                                            background: 'rgba(0, 102, 255, 0.02)',
                                            color: 'var(--primary)',
                                            fontSize: '0.85rem',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        + Add Tool
                                    </button>
                                </>
                            );
                        })()}
                    </FormGroup>
                </FormRow>

                <Footer>

                    <div style={{ flex: '1' }}>
                    </div>

                    <Button primary onClick={() => savePlanner()}>
                        <FontAwesomeIcon icon={faSave} /> Save Planner</Button>
                </Footer>

            </motion.div>

        </PageContainer>
    );
}

export default PlannerDetails;